import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiArrowDown, FiCheck, FiCheckCircle, FiFileText, FiInfo, FiShield } from "react-icons/fi";
import SideDrawer from "../SideDrawer/SideDrawer";
import { getLegalDocument } from "./legalContent";
import styles from "./LegalDrawer.module.css";

/**
 * LegalDrawer — klauzula RODO / regulamin w wysuwanym panelu z prawej, z BRAMKĄ SCROLLA:
 * przycisk „Akceptuję" jest nieaktywny, dopóki użytkownik nie przewinie treści do końca.
 *
 * Panel to istniejący components/SideDrawer (zamykany wyłącznie ✕ / przyciskiem — klik w tło
 * daje potrząśnięcie, warunek W-10 design systemu). Tu NIE tworzymy własnej szuflady.
 *
 * DLACZEGO IntersectionObserver, a nie scrollTop + clientHeight >= scrollHeight:
 * porównanie liczbowe zawodzi przy zoomie przeglądarki i skalowaniu systemowym (wysokości
 * wychodzą ułamkowe i suma bywa o 0,5 px za mała), przy pasku „bounce" na iOS oraz przy
 * elastycznych obrazkach. Wartownik (pusty div na końcu treści) obserwowany względem kontenera
 * przewijania jest odporny na te przypadki: albo wjechał w widok, albo nie.
 *
 * PRZYPADEK BRZEGOWY — treść KRÓTSZA niż panel: wartownik jest widoczny od razu, więc
 * IntersectionObserver odpala się przy pierwszym sprawdzeniu i bramka otwiera się natychmiast.
 * Dodatkowo mierzymy to jawnie (scrollHeight <= clientHeight), żeby nie zależeć wyłącznie
 * od kolejności zdarzeń — bez tego przy krótkim dokumencie przycisk NIGDY by się nie odblokował.
 *
 * API:
 *   <LegalDrawer
 *       isOpen={bool}
 *       onClose={() => void}
 *       kind="rodo" | "regulamin"
 *       onAccept={() => void}     // wołane po kliknięciu „Akceptuję" (po przewinięciu do końca)
 *       accepted={bool}           // dokument już zaakceptowany — panel pokazuje potwierdzenie
 *   />
 */

/** Kontener przewijania: .body z SideDrawer. Szukamy go po overflow, nie po klasie CSS-modules. */
const findScrollParent = (node) => {
    let el = node?.parentElement;
    while (el && el !== document.body) {
        const style = window.getComputedStyle(el);
        if (style.overflowY === "auto" || style.overflowY === "scroll" || style.overflow === "auto") {
            return el;
        }
        el = el.parentElement;
    }
    return null;
};

const Block = ({ block }) => {
    switch (block.type) {
        case "sub":
            return <h4 className={styles.sub}>{block.text}</h4>;
        case "ul":
            return (
                <ul className={styles.ul}>
                    {block.items.map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            );
        case "ol":
            return (
                <ol className={styles.ol}>
                    {block.items.map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ol>
            );
        case "dl":
            return (
                <dl className={styles.dl}>
                    {block.items.map((item, i) => (
                        <div className={styles.dlRow} key={i}>
                            <dt className={styles.dt}>{item.term}</dt>
                            <dd className={styles.dd}>{item.text}</dd>
                        </div>
                    ))}
                </dl>
            );
        case "note":
            return <p className={styles.note}>{block.text}</p>;
        case "p":
        default:
            return <p className={styles.p}>{block.text}</p>;
    }
};

const LegalDrawer = ({
    isOpen,
    onClose,
    kind = "rodo",
    onAccept,
    accepted = false,
    /**
     * Panel otwierany z kreatora osadzonego w oknie modal (rejestr przepustek). Bez tego
     * dokument renderuje się POD oknem i klik w „Przeczytaj i zaakceptuj" wygląda na martwy.
     * W portalu publicznym kreator nie jest modalem, więc domyślnie zostaje zwykła warstwa.
     */
    elevated = false,
}) => {
    const doc = getLegalDocument(kind);

    const [reachedEnd, setReachedEnd] = useState(false);
    const [progress, setProgress] = useState(0);
    const [contentEl, setContentEl] = useState(null);

    const sentinelRef = useRef(null);
    const scrollRootRef = useRef(null);

    // Nowy dokument albo ponowne otwarcie panelu = bramka zamknięta od nowa.
    useEffect(() => {
        setReachedEnd(false);
        setProgress(0);
    }, [kind, isOpen]);

    // Pomiar postępu czytania + wykrycie końca treści.
    useEffect(() => {
        if (!isOpen || !contentEl) return undefined;

        const scrollRoot = findScrollParent(contentEl);
        scrollRootRef.current = scrollRoot;

        const measure = () => {
            if (!scrollRoot) {
                // Brak kontenera przewijania = cała treść widoczna.
                setProgress(100);
                setReachedEnd(true);
                return;
            }
            const { scrollTop, clientHeight, scrollHeight } = scrollRoot;
            const scrollable = scrollHeight - clientHeight;
            if (scrollable <= 4) {
                // Treść krótsza niż panel — inaczej przycisk nigdy by się nie odblokował.
                setProgress(100);
                setReachedEnd(true);
                return;
            }
            const pct = Math.round((scrollTop / scrollable) * 100);
            setProgress(Math.max(0, Math.min(100, pct)));
            // Zapas ratunkowy na wypadek, gdyby wartownik nie zadziałał (stara przeglądarka).
            if (scrollTop >= scrollable - 2) setReachedEnd(true);
        };

        // Zmiana dokumentu przy otwartym panelu: wracamy na początek. Bez tego panel zostaje
        // przewinięty w miejscu ze starej treści, a licznik startuje od losowego procenta.
        if (scrollRoot && scrollRoot.scrollTop !== 0) {
            scrollRoot.scrollTop = 0;
        }

        measure();

        const target = scrollRoot || window;
        target.addEventListener("scroll", measure, { passive: true });
        window.addEventListener("resize", measure);

        let resizeObserver = null;
        if (typeof ResizeObserver === "function" && scrollRoot) {
            // Obrazki/fonty potrafią dołożyć wysokości już po pierwszym pomiarze.
            resizeObserver = new ResizeObserver(measure);
            resizeObserver.observe(contentEl);
            resizeObserver.observe(scrollRoot);
        }

        let observer = null;
        if (typeof IntersectionObserver === "function" && sentinelRef.current) {
            observer = new IntersectionObserver(
                (entries) => {
                    if (entries.some((entry) => entry.isIntersecting)) {
                        setReachedEnd(true);
                        setProgress(100);
                    }
                },
                { root: scrollRoot || null, threshold: 0.01 }
            );
            observer.observe(sentinelRef.current);
        }

        return () => {
            target.removeEventListener("scroll", measure);
            window.removeEventListener("resize", measure);
            resizeObserver?.disconnect();
            observer?.disconnect();
        };
    }, [isOpen, contentEl, kind]);

    const scrollToEnd = useCallback(() => {
        const sentinel = sentinelRef.current;
        const scrollRoot = scrollRootRef.current;
        if (sentinel?.scrollIntoView) {
            sentinel.scrollIntoView({ behavior: "smooth", block: "end" });
            return;
        }
        if (scrollRoot) {
            scrollRoot.scrollTo({ top: scrollRoot.scrollHeight, behavior: "smooth" });
        }
    }, []);

    const canAccept = accepted || reachedEnd;
    const shownProgress = accepted ? 100 : progress;

    const footer = (
        <div className={styles.footer}>
            <div className={styles.progressRow}>
                <div
                    className={styles.progressTrack}
                    role="progressbar"
                    aria-label="Postęp czytania dokumentu"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={shownProgress}
                >
                    <div
                        className={`${styles.progressFill} ${canAccept ? styles.progressFillDone : ""}`}
                        style={{ width: `${shownProgress}%` }}
                    />
                </div>
                <span className={styles.progressLabel}>
                    {canAccept ? "Przeczytano całość" : `Przeczytano ${shownProgress}%`}
                </span>
            </div>

            {!canAccept && (
                <p className={styles.gateHint} id="legal-gate-hint" role="status">
                    {`Przewiń dokument do końca, aby odblokować przycisk „${doc.acceptLabel || "Akceptuję"}”.`}
                </p>
            )}

            {accepted && (
                <p className={styles.acceptedHint} role="status">
                    <FiCheckCircle size={14} aria-hidden="true" /> Dokument został już zaakceptowany.
                </p>
            )}

            <div className={styles.actions}>
                {!reachedEnd && !accepted && (
                    <button type="button" className={styles.ghostBtn} onClick={scrollToEnd}>
                        <FiArrowDown size={15} aria-hidden="true" /> Przewiń na dół
                    </button>
                )}
                <button type="button" className={styles.ghostBtn} onClick={onClose}>
                    Zamknij
                </button>
                <button
                    type="button"
                    className={styles.acceptBtn}
                    onClick={onAccept}
                    disabled={!canAccept}
                    aria-describedby={!canAccept ? "legal-gate-hint" : undefined}
                >
                    <FiCheck size={16} aria-hidden="true" /> {doc.acceptLabel || "Akceptuję"}
                </button>
            </div>
        </div>
    );

    return (
        <SideDrawer
            isOpen={isOpen}
            onClose={onClose}
            title={doc.title}
            subtitle={doc.subtitle}
            icon={kind === "regulamin" ? <FiFileText size={18} /> : <FiShield size={18} />}
            footer={footer}
            width="640px"
            elevated={elevated}
        >
            <div className={styles.doc} ref={setContentEl}>
                {/* Stały pasek informacyjny zamiast dawnego toastu przy otwarciu panelu:
                    toast zasłaniał treść, którą właśnie kazał przeczytać. Pasek znika,
                    gdy bramka scrolla jest już otwarta — komunikat przestaje być prawdą. */}
                {!canAccept && (
                    <div className={styles.gateBar} role="note">
                        <FiInfo size={15} aria-hidden="true" />
                        <span>
                            Przeczytaj dokument do końca — przycisk akceptacji odblokuje się
                            na dole panelu.
                        </span>
                    </div>
                )}
                <div className={styles.meta}>
                    <span className={styles.metaChip}>Wersja {doc.version}</span>
                    <span className={styles.metaChip}>Aktualizacja: {doc.updatedAt}</span>
                </div>
                {doc.basis && <p className={styles.basis}>{doc.basis}</p>}

                {doc.lead && <p className={styles.lead}>{doc.lead}</p>}

                {doc.sections.map((section) => (
                    <section className={styles.section} key={section.id}>
                        <h3 className={styles.heading}>{section.heading}</h3>
                        {section.blocks.map((block, i) => (
                            <Block block={block} key={`${section.id}-${i}`} />
                        ))}
                    </section>
                ))}

                {doc.acceptSummary && (
                    <div className={styles.summaryBox}>
                        <h4 className={styles.summaryTitle}>Treść akceptacji</h4>
                        <p className={styles.summaryText}>{doc.acceptSummary}</p>
                    </div>
                )}

                <p className={styles.approval}>{doc.approvalNote}</p>

                {/* Wartownik bramki scrolla — wjeżdża w widok dopiero na samym końcu dokumentu. */}
                <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />
            </div>
        </SideDrawer>
    );
};

export default LegalDrawer;
